import { FC, useMemo } from 'react'
import * as cn from 'classnames'
import { isMobileSelector } from 'selectors/viewportSelectors'
import { IS_IE_DESKTOP, IS_IE_MOBILE } from 'helpers/userAgentHelpers'
import { cutContent } from 'helpers/formatHelpers'
import { PRESS_PAGE as PressPageName } from 'constants/pages'
import Anchor from 'components/atoms/navigation/Anchor'
import { ExternalPageHeading } from 'components/atoms/Text'
import ExternalLayout from 'components/features/external/ExternalLayout/ExternalLayout'
import { CardBlock } from 'components/features/pfm/Blocks/Blocks'
import { PRESS_EMAIL } from 'constants/email'
import TextButton from 'components/atoms/buttons/TextButton'
import * as moment from 'moment'
import * as orderBy from 'lodash/orderBy'
import { EmailLink } from 'components/features/pfm/EmailLink/EmailLink'
import * as styles from 'components/features/pfm/PressPage/PressPage.styl'
import { useStoreSelector } from 'helpers/reactReduxHelpers'
import { PageMeta } from 'components/features/pfm/PageMeta/PageMeta'

const MOBILE_MAX_CONTENT_LENGTH = 300
const DESKTOP_MAX_CONTENT_LENGTH = 500
const PERMISSIBLE_ERROR = 30

type Article = {
  url: string
  logo: string
  date: string
  title: string
  children: string
}

const ARTICLES: Article[] = [
  {
    url: 'https://www.pymnts.com/cryptocurrency/2021/railsbank-status-money-partner-on-crypto-investment-card/',
    logo: 'pymnts.png',
    date: '2021-08-31',
    title: 'Railsbank, Status Money Partner On Crypto Investment Card',
    children: 'The embedded finance platform Railsbank is working with the personal finance company Status Money to launch a cash-back credit card that invests rewards in cryptocurrency.',
  },

  {
    url: 'https://money.com/how-to-form-a-savings-group/',
    logo: 'moneycom.png',
    date: '2021-05-14',
    title: 'The Secret to Saving More Money? Team up With Friends, Family or Even Complete Strangers',
    children: 'Studies have shown that peer pressure can get you to exercise more or quit smoking. It may also help you save money.',
  },

  {
    url: 'https://grow.acorns.com/coronavirus-vacations/?&qsearchterm=status%20money',
    logo: 'acorns_cnbc.png',
    date: '2020-09-04',
    title: '7 charts show how the coronavirus pandemic has changed vacations',
    children: 'Of the many activities that Covid-19 made riskier, travel is probably both the most anticipated and the priciest. From March 9 to August 24 of this year, Americans spent an average of $635 on travel, according to Status Money.',
  },

  {
    url: 'https://learnbonds.com/news/status-money-founder-millennials-not-equipped-to-make-fundamental-financial-decisions/',
    logo: 'learn_bonds_logo.png',
    date: '2020-06-10',
    title: 'Status Money founder: Millennials not equipped to make fundamental financial decisions',
    children: 'A dismal 17% of millennials in the US have basic financial literacy. This means that they’re not equipped to make fundamental financial decisions about how to borrow, spend, save, and invest their money.',
  },

  {
    url: 'https://finovate.com/status-money-launches-premium-membership/',
    logo: 'finovate.jpg',
    date: '2020-06-09',
    title: 'Status Money Launches Premium Membership',
    children: 'The app will still offer free access but the premium membership unlocks advanced features and the ability to earn cash rewards for simply using the app.',
  },


  {
    url: 'https://www.businesswire.com/news/home/20200127005123/en/Status-Money-Announces-New-Investor',
    logo: 'businesswire.png',
    date: '2020-01-27',
    title: 'Status Money Announces New Investor',
    children: 'Status Money announced today that it has completed a convertible note financing agreement with SixThirty Ventures. The financing brings Status Money’s total funding to $9.5M since it was founded in 2016.',
  },
  {
    url: 'https://tearsheet.co/marketing/5-techniques-fintech-and-banking-apps-use-to-engage-and-retain-users/',
    logo: 'tearsheet.png',
    date: '2020-01-30',
    title: '5 techniques fintech and banking apps use to engage and retain users',
    children: 'The new Status Money app is a platform where you can compare your spending to those of similar backgrounds with similar incomes — all anonymously. It presents budgeting like a competition, giving virtual badges when users receive likes and comments on their posts, and when they follow the app’s advice to cut back on their spending or save more each month.',
  },
  {
    url: 'https://republic.co/blog/majd-maksad-status-money',
    logo: 'republic.png',
    date: '2020-01-29',
    title: 'An Interview with Status Money’s CEO',
    children: 'Billions of dollars were invested in Fintech in the last decade and yet the situation has only gotten worse. Personal loans, student loans, and other consumer debt are now at all-time highs. It took a lot of thinking and research to hone in on what I consider to be the underlying cause: people’s lack of access to financial information and advice.',
  },
  {
    url: 'https://www.prnewswire.com/news-releases/status-money-and-centsai-announce-partnership-to-expand-access-to-financial-education-301002853.html?tc=eml_cleartime',
    logo: 'prnewswire.png',
    date: '2020-02-11',
    title: 'Status Money and CentSai Announce Partnership to Expand Access to Financial Education',
    children: 'Financial education has never been more important, with U.S. household debt hitting an all-time high of $14 trillion ($1.3 trillion higher than the previous peak in 2008) and only 17 percent of Americans aged 18 to 34 having basic financial literacy, according to FINRA\'s latest Financial Capability Study.',
  },
  {
    url: 'https://www.cnn.com/2020/02/18/success/money-comparison-apps-venmo-status-money/index.html',
    logo: 'cnn_business.png',
    date: '2020-02-18',
    title: 'These apps are changing the way we talk about money',
    children: 'While the conspicuous consumption of those around us can be fairly evident — the neighbor\'s fancy new car, for example — we often don\'t know the full picture of our peers\' finances, like how much they earn, what they spend on groceries or their debt load. But Status Money gives you some visibility into those unknowns.',
  },
  {
    url: 'https://www.mic.com/p/money-diaries-salary-lists-our-obsession-with-other-peoples-money-19376567',
    logo: 'mic.png',
    date: '2019-11-25',
    title: 'Why we\'re so obsessed with how other people spend their money',
    children: 'My obsession with other people’s financial situations always felt a bit lascivious, dirty even, like watching porn in the morning. The only moral issue behind this feeling, though, is a social construct. Like many, I was raised to believe that it was rude to discuss money openly.',
  },
  {
    url: 'https://nypost.com/2019/09/03/amazon-testing-payment-system-that-uses-hands-as-id/',
    logo: 'nypost.svg',
    date: '2019-09-03',
    title: 'Amazon tests Whole Foods payment system that uses hands as ID',
    children: 'Retailers have always been interested in faster checkout,” Majd Maksad, founder and CEO of Status Money, a personal finance site, told The Post. “You only have to walk into Whole Foods to see the massive lines of people waiting to check out. It’s a massive friction point.',
  },
  {
    url: 'https://grow.acorns.com/how-to-resist-peer-pressure-and-keep-from-overspending/',
    logo: 'acorns_cnbc.png',
    date: '2019-10-23',
    title: 'How to resist peer pressure and keep yourself from overspending',
    children: 'Consumers who find out they spend more money than peers of similar income tend to cut back their spending. That’s according to research done by the University of Chicago with data from Status Money, a platform where consumers can compare their finances to those who make similar amounts of money.',
  },
  {
    url: 'https://www.wsj.com/articles/peer-pressure-can-improve-your-financial-life-study-finds-11552328444',
    logo: 'wall_street_hor.svg',
    date: '2019-03-11',
    title: 'Peer Pressure Can Improve Your Financial Life, Study Finds',
    children: 'A new paper by Francesco D’Acunto at Boston College, Alberto Rossi of the University of Maryland and Michael Weber of the University of Chicago finds that people cut their spending—sometimes drastically—when they are told they have been spending more than others in similar circumstances.',
  },
  {
    url: 'https://www.businessinsider.com/status-money-to-offer-cash-rewards-to-improve-your-finances-2018-12',
    logo: 'businessinsider.svg',
    date: '2018-12-12',
    title: 'This startup plans to take on CreditKarma by offering cash rewards for making smarter money decisions',
    children: 'A startup plans to pay you for opening a credit card with a lower rate. Or moving your money into a higher-yielding savings account. Or using a coupon at your favorite retailer. The service is the brainchild of Status Money, a relatively new player in an industry led by larger firms like Mint or CreditKarma that recommend credit card offers, high-yield savings accounts or online personal loans within free personal finance software. In return for sharing their data, consumers get access to offers. The tech companies receive what is effectively a referral fee.',
  },
  {
    url: 'https://www.marketwatch.com/story/curious-how-much-your-peers-are-making-and-spending-these-companies-will-tell-you-2018-07-24',
    logo: 'marketwatch.svg',
    date: '2018-07-28',
    title: 'Are you spending more than your peers? These companies will tell you',
    children: 'Josh Hastings and his wife Lauren are facing student loans of almost $300,000. Hastings, 31, a teacher in the suburbs of Washington, D.C., and Lauren, 30, a physical therapist, recently decided it was time to get serious about paying off their student debt after she completed her doctorate. Their first priority: Stop worrying about what others thought about them.',
  },
  {
    url: 'https://www.forbes.com/sites/amitchowdhry/2018/03/15/how-status-money-is-making-it-easier-for-people-to-compare-finances',
    logo: 'forbes.png',
    date: '2018-03-15',
    title: 'How Status Money Is Making It Easier For People To Compare Finances',
    children: 'Status Money is a personal financial management service that makes it easier for people to anonymously compare their finances to millions of other people for free. By comparing your finances to other people, you can set benchmarks and come up with strategies to improve your own situation. Many people unknowingly overpay for financial services and lose millions of dollars in interest rates and hidden fees...',
  },
  {
    url: 'http://money.cnn.com/2017/11/01/pf/fintech-share/index.html',
    logo: 'cnnmoney.svg',
    date: '2017-11-01',
    title: 'Millennials want to share and compare everything – even finances',
    children: 'You already comparison shop on Google. You rely on the "customers also bought" items popping up beneath your Amazon purchase as informed options. You crowd-source all manner of daily dilemmas on your social feeds from what to wear, to what to eat, to where to travel.',
  },
  {
    url: 'https://www.forbes.com/sites/elizabethharris/2018/05/09/how-peer-pressure-could-help-boost-savings',
    logo: 'forbes.png',
    date: '2018-05-09',
    title: 'How Peer Pressure Could Help Boost Savings',
    children: 'In the depths of the Great Recession, Majd Maksad, a data analyst was examining reams of data on Americans’ finances as an analyst for Citi, when he made a surprising observation. Even as many economists, pundits and individuals credited the financial crisis and its aftermath for ushering in a new era of greater frugality and leading to what many heralded as a healthy deleveraging of household balance sheets, Maksad didn’t see that in the data. Instead, around 2010, his analysis showed continued spending. And while there was deleveraging, Maksad found that it was driven largely by writing off bad debts through foreclosures and personal bankruptcies, not necessarily personal choice.',
  },
  {
    url: 'https://www.marketwatch.com/story/peer-pressure-can-make-you-rich-or-save-you-money-2018-05-03',
    logo: 'marketwatch.svg',
    date: '2018-05-06',
    title: 'How peer pressure can actually save you quite a lot of money',
    children: 'Consumers who realize they’re spending more than their peers—those who are of similar ages, incomes, locations and credit scores—will actually cut back on their spending, researchers at the University of Chicago’s Booth School of Business and the University of Maryland’s Smith School of Business found. Their study will be published as an academic paper later this summer.',
  },
  {
    url: 'http://fortune.com/2018/02/27/top-cryptocurrency-cities-jacksonville-bitcoin-ethereum-ripple/',
    logo: 'fortune.svg',
    date: '2018-02-27',
    title: 'These U.S. Cities Have the Most Crypto Investments Per Person',
    children: 'When you think about who’s buying Bitcoin, Ethereum, Ripple, and other cryptocurrencies, it’s natural for your mind to wander to tech hubs and big cities. Jacksonville, Fla. probably wouldn’t be the first city to spring to mind.',
  },
  {
    url: 'http://abc7news.com/finance/financial-website-vows-to-save-users-money-/3013823/',
    logo: 'abc7news.png',
    date: '2018-01-31',
    title: 'New comparative financial website vows to save users money',
    children: 'San Francisco (KGO) – A new financial website may be able to save you money in areas you may not have considered. Many websites offer general financial advice. But what makes this site unique - the suggestions are customized for your specific situation. And it’s free.',
  },
  {
    url: 'http://www.marketwatch.com/story/when-higher-credit-scores-dont-always-mean-lower-interest-rates-2017-10-10?mod=mw_share_twitter',
    logo: 'marketwatch.svg',
    date: '2017-10-15',
    title: 'When higher credit scores don’t always mean lower interest rates',
    children: 'In an ideal world, an excellent credit score should mean you get a lower interest rate than people who have worse scores. After all, isn’t that why people strive to pay off their credit-card bills on time? In the real world, however, that’s not always the case.',
  },
  {
    url: 'https://www.cnbc.com/video/2018/04/17/fortt-knox-tech-for-tax-day.html',
    logo: 'cnbc.svg',
    date: '2018-04-17',
    title: 'Fortt Knox: Tech for tax day',
    children: 'CNBC’s Jon Fortt and Sharon Epperson discuss tax day and smart ways tech can help you save money in 2018 with Status Money CEO Majd Maksad.',
  },
  {
    url: 'https://www.businesswire.com/news/home/20170905005522/en/Citi-Goldman-Sachs-Bankers-Launch-Status-Money',
    logo: 'businesswire.png',
    date: '2017-09-05',
    title: 'Former Citi and Goldman Sachs Bankers Launch Status Money, PFM Website That Lets People Compare Their Finances',
    children: 'Two former Citi and Goldman Sachs bankers have launched Status Money (statusmoney.com) - the first and only personal financial management (PFM) website that lets everyone anonymously compare their finances to millions of other people for free.',
  },
  {
    url: 'https://money.cnn.com/2018/02/26/pf/credit-card-rising-rates/index.html',
    logo: 'cnnmoney.svg',
    date: '2018-02-27',
    title: 'What you don’t know about your credit card interest rates',
    children: 'Don’t know? You’re not alone, and you’re also not likely to know if your rate goes up. "Many people don’t know their interest rate," says Majd Maksad, a former Citi executive and CEO of Status Money, a site that helps people compare their financial picture, including credit card interest rates, with information from more than 16 million Americans. "You tell them it’s 5%, but they still don’t know if they feel good or bad about it. By looking at others’ interest rates you can say \'Great! I did well\' or \'I can do better\'."',
  },
  {
    url: 'https://www.bankrate.com/personal-finance/smart-money/fix-your-finances-by-comparing-your-spending-levels-with-others/',
    logo: 'bankrate.png',
    date: '2018-04-10',
    title: 'Want to fix your finances? Compare how you spend with others',
    children: 'How much should you have in savings by the time you’re 30? What is the right amount to spend on food each month? What is a good credit score for a millennial? People are obsessed with how they are doing financially, and especially how they’re doing compared to others.',
  },
  {
    url: 'https://www.logicworks.com/blog/2017/10/data-science-personal-finance-aws-cloud-status-money/',
    logo: 'logicworks.png',
    date: '2017-10-27',
    title: 'Status Money Partners With Logicworks To Help Consumers Save Money',
    children: 'Have you ever wondered how your finances compare to those of other people? Do you know if you’re overspending, not saving enough, or paying higher interest rates than people like you? Data scientists Majd Maksad and Korash Hernandez know the answers. After working at companies like Discover Card, Citigroup, and Goldman Sachs, they have now launched Status Money (statusmoney.com) – the first personal finance management company that lets you anonymously compare your finances to millions of other people.',
  },
  {
    url: 'https://www.benzinga.com/markets/cryptocurrency/18/02/11236807/top-10-cryptocurrency-cities-by-volume-according-to-statusmone',
    logo: 'benzinga.png',
    date: '2018-02-22',
    title: 'Top 10 Cryptocurrency Cities By Volume, According To StatusMoney',
    children: 'Citizens of Jacksonville, Florida have developed quite the appetite for cryptocurrency. In December, when cryptomania seemingly hit an all-time high, crypto buyers in Jacksonville moved the most money into cryptocurrency exchanges, totaling $31,468 per person on average, the highest in the nation and over twice the amount of its closest competitor, Memphis, Tennessee, according to a new report from StatusMoney. The figures are based on crypto activity per person in December 2017.',
  },
  {
    url: 'https://www.abqjournal.com/1138496/abq-ranked-no-3-for-cryptocurrency-investment.html',
    logo: 'abqjournal.png',
    date: '2018-02-26',
    title: 'ABQ ranked No. 3 for cryptocurrency investment',
    children: 'ALBUQUERQUE, N.M. — Albuquerque has one of the nation’s highest volumes of cryptocurrency activity per investor, according to a recent report. The report was compiled by financial technology company Status Money, which said in a release that it “tracked the volume of crypto transactions from a panel of millions of Americans” to determine the ranking. The data was based on December 2017 transactions.',
  },
  {
    url: 'https://www.nbcnews.com/better/business/biometrics-are-here-crazy-ways-you-re-going-be-paying-ncna872336',
    logo: 'better.svg',
    date: '2018-05-09',
    title: 'Biometrics are here: The crazy ways you’re going to be paying in the future',
    children: 'Fingerprints, your voice, your face, the pattern of veins in the palm of your hand and even the colored pattern in your iris: All of these are being used in biometrics, the analysis of unique physical characteristics to verify identity. For decades, we watched Bond villains and Hollywood heads of state use these impressively futuristic methods to further their quests for world domination, but today we’re using them to buy coffee. (Take that, Jason Bourne!)',
  },
  {
    url: 'https://www.bankrate.com/personal-finance/save-money-without-feeling-fomo/',
    logo: 'bankrate.png',
    date: '2019-02-08',
    title: 'How to keep saving in an age of FOMO pressures',
    children: 'You meant to put a small deposit into your savings account. But your dear friend unexpectedly came into town. You didn’t want to miss out on catching up even though it meant spending more on a meal than you intended. Savings can wait until next month, you think — when you might even make more money.',
  },
  {
    url: 'https://finance.yahoo.com/news/partnership-expands-opportunities-personal-finance-170000528.html',
    logo: 'yahoo.svg',
    date: '2019-02-01',
    title: 'New Partnership Expands Opportunities in Personal Finance',
    children: 'To better serve its members, Status Money, a personal finance app that offers cash incentives for better financial habits, has announced a payments partnership with Dwolla, Inc. Status Money is a free platform that uses machine learning algorithms to find opportunities for its members to save and earn money. Members earn cash rewards for joining, setting up an account and for engaging with opportunities offered through the app.',
  },
  {
    url: 'https://www.prnewswire.com/news-releases/plug-and-play-announces-the-135-startups-accepted-into-their-spring-batches-300812121.html',
    logo: 'plugandplay.svg',
    date: '2019-03-14',
    title: 'Plug and Play Announces The 135 Startups Accepted Into Their Spring Batches',
    children: 'Plug and Play has selected 135 startups to participate in their Spring 2019 accelerator batches. Entrepreneurs from each startup will take part in one of the following programs: Brand & Retail, Cybersecurity, Energy & Sustainability, Fintech, Food & Beverage, New Materials & Packaging, and Supply Chain. The full list of selected startups can be viewed on Plug and Play\'s website.',
  },
  {
    url: 'https://www.benzinga.com/fintech/21/04/20540667/how-status-money-is-rewriting-the-fintech-playbook',
    logo: 'benzinga.png',
    date: '2021-04-08',
    title: 'How Status Money Is Rewriting the Fintech Playbook',
    children: 'Unlike fintechs that adopted the unbundling and rebundling strategy, or in Maksad’s words, the “divide and conquer” strategy, Status Money adopted a marketplace strategy. Status Money’s strategy is to offer a differentiated suite of free financial management tools, create a marketplace for financial products from partner institutions, then introduce its own financial products in key verticals.',
  },
]

const PressCard = ({ url, logo, date, title, children }: Article) => (
  <div className={ styles.cardWrapper }>
    <CardBlock withPadding shadowTheme='soft'>
      <header className={ styles.cardHeader }>
        <a
          href={ url }
          target='_blank'
          className={
            cn({
              [styles.cardLogoLinkIE]: IS_IE_DESKTOP || IS_IE_MOBILE,
            })
          }
        >
          <img className={ styles.cardLogo } src={ `/images/media-logos/${ logo }` } />
        </a>
        <div className={ styles.cardDate }>
          { moment(date).format('MMMM D, YYYY') }
        </div>
      </header>
      <h4 className={ styles.cardTitle }>
        { title }
      </h4>
      <p className={ styles.cardParagraph }>
        { children }
      </p>
      <footer className={ styles.cardFooter }>
        <a href={ url } target='_blank'>
          <TextButton size='large'>Read More</TextButton>
        </a>
      </footer>
    </CardBlock>
  </div>
)

export const PressPage: FC = () => {
  const isMobile = useStoreSelector(isMobileSelector)
  const articles = useMemo(
    () => orderBy(ARTICLES, ['date'], ['desc']),
    []
  )

  const maxContentLength = isMobile ? MOBILE_MAX_CONTENT_LENGTH : DESKTOP_MAX_CONTENT_LENGTH

  return (
    <Anchor placement={ PressPageName }>
      <PageMeta
        title='Press | Status Money'
        description='We democratize access to financial advice. See what the press is saying about Status Money.'
      />
      <ExternalLayout
        type='common'
        isSingleColumn
      >
        <section className={ styles.section }>
          <ExternalPageHeading>Press</ExternalPageHeading>
        </section>
        <div className={ styles.pressEmailContainer }>
          For inquires, please email us at <EmailLink email={ PRESS_EMAIL } />
        </div>
        { articles.map((article) => (
          <PressCard { ...article } key={ article.url }>
            { cutContent(article.children, maxContentLength, PERMISSIBLE_ERROR) }
          </PressCard>
        )) }
      </ExternalLayout>
    </Anchor>
  )
}
